import React, { useState, useEffect, useContext } from 'react';
import {
    CCard,
    CCardBody,
    CCardHeader,
    CButton,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CSpinner,
    CAlert,
    CBadge,
    CRow,
    CCol
} from '@coreui/react';
import { AuthUserContext } from '../../session';
import axios from 'axios';
import config from '../../../utils/EnvironmentSwitcher';
import './Horoscope.scss';
import { getAuth } from 'firebase/auth';

/**
 * Horoscope Component - Daily horoscope with birthdate collection
 * 
 * Features:
 * - Birthdate collection and FHIR storage
 * - Automatic zodiac sign detection
 * - Daily horoscope fetching
 * - Zodiac compatibility
 * - Personalized astrological insights
 */
const Horoscope = () => {
    const { authUser } = useContext(AuthUserContext);
    const [birthdate, setBirthdate] = useState('');
    const [zodiacSign, setZodiacSign] = useState('');
    const [horoscope, setHoroscope] = useState('');
    const [showBirthdateModal, setShowBirthdateModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [hasStoredBirthdate, setHasStoredBirthdate] = useState(false);
    
    // Mobile date picker state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    const [selectedYear, setSelectedYear] = useState('');

    // Zodiac signs data
    const zodiacSigns = {
        aries: { name: 'Aries', dates: 'Mar 21 - Apr 19', symbol: '♈', element: 'Fire' },
        taurus: { name: 'Taurus', dates: 'Apr 20 - May 20', symbol: '♉', element: 'Earth' },
        gemini: { name: 'Gemini', dates: 'May 21 - Jun 20', symbol: '♊', element: 'Air' },
        cancer: { name: 'Cancer', dates: 'Jun 21 - Jul 22', symbol: '♋', element: 'Water' },
        leo: { name: 'Leo', dates: 'Jul 23 - Aug 22', symbol: '♌', element: 'Fire' },
        virgo: { name: 'Virgo', dates: 'Aug 23 - Sep 22', symbol: '♍', element: 'Earth' },
        libra: { name: 'Libra', dates: 'Sep 23 - Oct 22', symbol: '♎', element: 'Air' },
        scorpio: { name: 'Scorpio', dates: 'Oct 23 - Nov 21', symbol: '♏', element: 'Water' },
        sagittarius: { name: 'Sagittarius', dates: 'Nov 22 - Dec 21', symbol: '♐', element: 'Fire' },
        capricorn: { name: 'Capricorn', dates: 'Dec 22 - Jan 19', symbol: '♑', element: 'Earth' },
        aquarius: { name: 'Aquarius', dates: 'Jan 20 - Feb 18', symbol: '♒', element: 'Air' },
        pisces: { name: 'Pisces', dates: 'Feb 19 - Mar 20', symbol: '♓', element: 'Water' }
    };

    useEffect(() => {
        if (authUser) {
            loadStoredBirthdate();
        }
    }, [authUser]);

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (zodiacSign) {
            fetchHoroscope();
        }
    }, [zodiacSign]);

    // Load stored birthdate from FHIR
    const loadStoredBirthdate = async () => {
        try {
            setIsLoading(true);
            // Use Firebase ID token so backend can verify via Admin SDK
            const auth = getAuth();
            const token = await (auth.currentUser?.getIdToken?.() ?? Promise.resolve(null));
            if (!token) {
                throw new Error('Unable to acquire Firebase ID token. Please login again.');
            }
            
            // Search for existing Patient resource with birthdate
            const response = await axios.get(
                `${config.fhirUrl}/fhir/Patient?identifier=${authUser.uid}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/fhir+json'
                    }
                }
            );

            if (response.data.entry && response.data.entry.length > 0) {
                const patient = response.data.entry[0].resource;
                if (patient.birthDate) {
                    setBirthdate(patient.birthDate);
                    const sign = getZodiacSign(new Date(patient.birthDate));
                    setZodiacSign(sign);
                    setHasStoredBirthdate(true);
                }
            }
        } catch (error) {
            console.error('Error loading birthdate:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate zodiac sign from birthdate
    const getZodiacSign = (date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();

        if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'aries';
        if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'taurus';
        if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'gemini';
        if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'cancer';
        if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'leo';
        if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'virgo';
        if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'libra';
        if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'scorpio';
        if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'sagittarius';
        if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'capricorn';
        if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'aquarius';
        if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return 'pisces';
        
        return '';
    };

    // Save birthdate to FHIR
    const saveBirthdateToFHIR = async (birthdateValue) => {
        try {
            setIsSaving(true);
            setError('');
            // Use Firebase ID token so backend can verify via Admin SDK
            const auth = getAuth();
            const token = await (auth.currentUser?.getIdToken?.() ?? Promise.resolve(null));
            if (!token) {
                throw new Error('Unable to acquire Firebase ID token. Please login again.');
            }

            // Build Patient resource in requested FHIR format
            const parseNameFromAuthUser = () => {
                const display = (authUser?.displayName || '').trim();
                if (display) {
                    const parts = display.split(/\s+/);
                    if (parts.length === 1) {
                        return { family: parts[0], given: [parts[0]] };
                    }
                    const family = parts[parts.length - 1];
                    const givenName = parts.slice(0, -1).join(' ');
                    return { family, given: [givenName] };
                }
                if (authUser?.email) {
                    const base = authUser.email.split('@')[0];
                    return { family: base, given: [base] };
                }
                return { family: 'User', given: ['User'] };
            };

            const { family, given } = parseNameFromAuthUser();

            // Create or update Patient resource (keeps identifier for lookup)
            const patientResource = {
                resourceType: 'Patient',
                identifier: [
                    {
                        system: 'https://fhir.mieros.cloud/fhir/ids',
                        value: authUser.uid
                    }
                ],
                name: [
                    {
                        family,
                        given
                    }
                ],
                gender: 'unknown',
                birthDate: birthdateValue,
                active: true
            };

            // Check if patient already exists
            const searchResponse = await axios.get(
                `${config.fhirUrl}/fhir/Patient?identifier=${authUser.uid}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/fhir+json'
                    }
                }
            );

            let response;
            if (searchResponse.data.entry && searchResponse.data.entry.length > 0) {
                // Update existing patient
                const existingPatient = searchResponse.data.entry[0].resource;
                patientResource.id = existingPatient.id;
                
                response = await axios.put(
                    `${config.fhirUrl}/fhir/Patient/${existingPatient.id}`,
                    patientResource,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/fhir+json'
                        }
                    }
                );
            } else {
                // Create new patient
                response = await axios.post(
                    `${config.fhirUrl}/fhir/Patient`,
                    patientResource,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/fhir+json'
                        }
                    }
                );
            }

            setSuccess('Birthdate saved successfully!');
            setHasStoredBirthdate(true);
            setTimeout(() => setSuccess(''), 3000);

        } catch (error) {
            console.error('Error saving birthdate to FHIR:', error);
            setError('Failed to save birthdate. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle birthdate submission
    const handleBirthdateSubmit = async () => {
        if (!birthdate) {
            setError('Please enter your birthdate');
            return;
        }

        const date = new Date(birthdate);
        if (isNaN(date.getTime())) {
            setError('Please enter a valid date');
            return;
        }

        const sign = getZodiacSign(date);
        setZodiacSign(sign);
        
        await saveBirthdateToFHIR(birthdate);
        setShowBirthdateModal(false);
    };

    // Fetch daily horoscope
    const fetchHoroscope = async () => {
        try {
            setIsLoading(true);
            
            // Sample horoscope data - replace with actual API
            const sampleHoroscopes = {
                aries: "Today brings exciting opportunities for new beginnings. Your natural leadership will shine through in group settings. Trust your instincts when making important decisions.",
                taurus: "Focus on stability and practical matters today. Your patience will be rewarded with steady progress. A financial opportunity may present itself.",
                gemini: "Communication is key today. Your wit and charm will open doors to new connections. Stay curious and embrace learning opportunities.",
                cancer: "Your intuition is particularly strong today. Trust your emotional intelligence when dealing with family matters. Home brings comfort and peace.",
                leo: "Your creativity is at its peak today. Express yourself boldly and don't be afraid to take center stage. Recognition for your efforts is coming.",
                virgo: "Attention to detail will serve you well today. Organize your thoughts and plans carefully. Health and wellness should be your priority.",
                libra: "Balance is essential today. Seek harmony in relationships and avoid conflicts. Your diplomatic skills will help resolve any tensions.",
                scorpio: "Deep transformation is possible today. Trust your inner wisdom and don't fear change. Secrets may be revealed that benefit you.",
                sagittarius: "Adventure calls to you today. Expand your horizons through travel or learning. Your optimism will inspire others around you.",
                capricorn: "Hard work pays off today. Your disciplined approach will lead to success. Focus on long-term goals and practical achievements.",
                aquarius: "Innovation and originality are your strengths today. Think outside the box and embrace your uniqueness. Technology may play a key role.",
                pisces: "Your compassion and empathy are needed today. Trust your dreams and intuitive insights. Creative pursuits will bring joy and fulfillment."
            };

            // Simulate API delay
            setTimeout(() => {
                setHoroscope(sampleHoroscopes[zodiacSign] || "The stars have a special message for you today. Stay open to new possibilities.");
                setIsLoading(false);
            }, 1000);

        } catch (error) {
            console.error('Error fetching horoscope:', error);
            setHoroscope("Unable to fetch your horoscope right now. Please try again later.");
            setIsLoading(false);
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    // Generate arrays for mobile date picker
    const getMonths = () => [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const getDays = () => {
        const days = [];
        for (let i = 1; i <= 31; i++) {
            days.push({ 
                value: i.toString().padStart(2, '0'), 
                label: i.toString() 
            });
        }
        return days;
    };

    const getYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 100; i--) {
            years.push({ value: i.toString(), label: i.toString() });
        }
        return years;
    };

    // Handle mobile date picker changes
    const handleMobileDateChange = () => {
        if (selectedMonth && selectedDay && selectedYear) {
            const dateString = `${selectedYear}-${selectedMonth}-${selectedDay}`;
            setBirthdate(dateString);
        }
    };

    // Initialize mobile date picker from existing birthdate
    const initializeMobileDatePicker = (dateString) => {
        if (dateString) {
            const [year, month, day] = dateString.split('-');
            setSelectedYear(year);
            setSelectedMonth(month);
            setSelectedDay(day);
        }
    };

    // Update mobile date picker when birthdate changes
    useEffect(() => {
        handleMobileDateChange();
    }, [selectedMonth, selectedDay, selectedYear]);

    // Initialize mobile picker when modal opens
    useEffect(() => {
        if (showBirthdateModal && birthdate) {
            initializeMobileDatePicker(birthdate);
        }
    }, [showBirthdateModal, birthdate]);

    return (
        <CCard className="horoscope-card">
            <CCardHeader className="horoscope-header">
                <div className="header-content">
                    <h3>🔮 Daily Horoscope</h3>
                    {zodiacSign && (
                        <CBadge color="primary" className="zodiac-badge">
                            {zodiacSigns[zodiacSign]?.symbol} {zodiacSigns[zodiacSign]?.name}
                        </CBadge>
                    )}
                </div>
            </CCardHeader>
            
            <CCardBody>
                {error && <CAlert color="danger">{error}</CAlert>}
                {success && <CAlert color="success">{success}</CAlert>}

                {!hasStoredBirthdate ? (
                    <div className="setup-section">
                        <div className="setup-content">
                            <div className="setup-icon">🌟</div>
                            <h4>Discover Your Daily Horoscope</h4>
                            <p>Enter your birthdate to receive personalized astrological insights and daily guidance.</p>
                            <CButton 
                                color="primary" 
                                onClick={() => setShowBirthdateModal(true)}
                                className="setup-btn"
                            >
                                Set Up Horoscope
                            </CButton>
                        </div>
                    </div>
                ) : (
                    <div className="horoscope-content">
                        {zodiacSign && (
                            <div className="zodiac-info">
                                <div className="zodiac-symbol">
                                    {zodiacSigns[zodiacSign]?.symbol}
                                </div>
                                <div className="zodiac-details">
                                    <h4>{zodiacSigns[zodiacSign]?.name}</h4>
                                    <p className="zodiac-dates">{zodiacSigns[zodiacSign]?.dates}</p>
                                    <CBadge color="info" className="element-badge">
                                        {zodiacSigns[zodiacSign]?.element} Sign
                                    </CBadge>
                                </div>
                            </div>
                        )}

                        <div className="daily-horoscope">
                            <h5>Today's Reading</h5>
                            {isLoading ? (
                                <div className="loading-horoscope">
                                    <CSpinner size="sm" className="me-2" />
                                    Reading the stars...
                                </div>
                            ) : (
                                <p className="horoscope-text">{horoscope}</p>
                            )}
                        </div>

                        <div className="horoscope-actions">
                            <CButton 
                                color="outline-primary" 
                                size="sm"
                                onClick={fetchHoroscope}
                                disabled={isLoading}
                            >
                                🔄 Refresh Reading
                            </CButton>
                            <CButton 
                                color="outline-secondary" 
                                size="sm"
                                onClick={() => setShowBirthdateModal(true)}
                            >
                                ⚙️ Update Birthdate
                            </CButton>
                        </div>

                        {birthdate && (
                            <div className="birthdate-info">
                                <small className="mb-2">
                                    Born: {formatDate(birthdate)}
                                </small>
                            </div>
                        )}
                    </div>
                )}
            </CCardBody>

            {/* Birthdate Input Modal */}
            <CModal visible={showBirthdateModal} onClose={() => setShowBirthdateModal(false)}>
                <CModalHeader>
                    <CModalTitle>Enter Your Birthdate</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <div className="birthdate-form">
                        <CFormLabel>
                            When were you born? 🎂
                        </CFormLabel>
                        
                        {isMobile ? (
                            // Mobile-friendly spinner date picker
                            <div className="mobile-date-picker">
                                <CRow>
                                    <CCol xs="4">
                                        <CFormLabel className="small-label">Month</CFormLabel>
                                        <CFormSelect
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            className="mobile-date-select"
                                        >
                                            <option value="">Month</option>
                                            {getMonths().map(month => (
                                                <option key={month.value} value={month.value}>
                                                    {month.label}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </CCol>
                                    <CCol xs="3">
                                        <CFormLabel className="small-label">Day</CFormLabel>
                                        <CFormSelect
                                            value={selectedDay}
                                            onChange={(e) => setSelectedDay(e.target.value)}
                                            className="mobile-date-select"
                                        >
                                            <option value="">Day</option>
                                            {getDays().map(day => (
                                                <option key={day.value} value={day.value}>
                                                    {day.label}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </CCol>
                                    <CCol xs="5">
                                        <CFormLabel className="small-label">Year</CFormLabel>
                                        <CFormSelect
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            className="mobile-date-select"
                                        >
                                            <option value="">Year</option>
                                            {getYears().map(year => (
                                                <option key={year.value} value={year.value}>
                                                    {year.label}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </CCol>
                                </CRow>
                                
                                {/* Preview selected date */}
                                {selectedMonth && selectedDay && selectedYear && (
                                    <div className="date-preview">
                                        <small className="text-info">
                                            📅 Selected: {formatDate(birthdate)}
                                        </small>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Desktop date picker
                            <CFormInput
                                type="date"
                                id="birthdate"
                                value={birthdate}
                                onChange={(e) => setBirthdate(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        )}
                        
                        <small className="mb-2 mt-2">
                            Your birthdate will be securely stored in your health record (FHIR) and used to provide personalized horoscope readings.
                        </small>
                    </div>
                </CModalBody>
                <CModalFooter>
                    <CButton 
                        color="secondary" 
                        onClick={() => setShowBirthdateModal(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </CButton>
                    <CButton 
                        color="primary" 
                        onClick={handleBirthdateSubmit}
                        disabled={(!birthdate || isSaving) || (isMobile && (!selectedMonth || !selectedDay || !selectedYear))}
                    >
                        {isSaving ? (
                            <>
                                <CSpinner size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            'Save & Get Horoscope'
                        )}
                    </CButton>
                </CModalFooter>
            </CModal>
        </CCard>
    );
};

export default Horoscope;


